// Sopwith 3D - plane movement, player control and plane lifecycle.
//
// Copyright (C) 1984-2000 David L. Clark
// Copyright (C) 2001-2026 Simon Howard and SDL Sopwith contributors
// Copyright (C) 2026 Sopwith 3D contributors
//
// This program is free software; you can redistribute it and/or modify it
// under the terms of the GNU General Public License as published by the Free
// Software Foundation; either version 2 of the License, or (at your option)
// any later version. This program is distributed WITHOUT ANY WARRANTY.
//
// Ported from SDL Sopwith swmove.c / swinit.c. The structure mirrors the C so
// the two can be compared line by line; comments explain intent.

import {
  ANGLES,
  FALLCOUNT,
  GRAVITY,
  K_ACCEL,
  K_BOMB,
  K_BREAK,
  K_DEACC,
  K_FLAPD,
  K_FLAPU,
  K_FLIP,
  K_HARRYKEYS,
  K_HOME,
  K_MISSILE,
  K_SHOT,
  K_STARBURST,
  MAX_NET_LAG,
  MAX_PLANES,
  MAX_THROTTLE,
  MAX_Y,
  MAXBOMBS,
  MAXBURSTS,
  MAXCRASH,
  MAXCRCOUNT,
  MAXFUEL,
  MAXMISSILES,
  MAXROUNDS,
  QUIT,
  SAFERESET,
  STALLCOUNT,
} from './constants';
import { clampMax, clampRange, COS, iabs, imax, inRange, setdxdy, SIN } from './fixed';
import { gohome, swauto } from './ai';
import { getAwards } from './medals';
import type { Game } from './game';
import { getSymset } from './symbols';
import { initbomb, initburst, initmiss, initshot, initsmok } from './weapons';
import { scorepln } from './scoring';
import { EndStatus, Faction, Obj, ObjState, ObjType, PlayMode, type OriginalOb } from './types';

// ---------------------------------------------------------------------------
// State predicates

export function planeIsKilled(s: ObjState): boolean {
  return (
    s !== ObjState.FLYING &&
    s !== ObjState.STALLED &&
    s !== ObjState.WOUNDED &&
    s !== ObjState.WOUNDSTALL
  );
}

export function planeIsStalled(s: ObjState): boolean {
  return s === ObjState.STALLED || s === ObjState.WOUNDSTALL;
}

export function planeIsWounded(s: ObjState): boolean {
  return s === ObjState.WOUNDED || s === ObjState.WOUNDSTALL;
}

export function planeIsFlying(s: ObjState): boolean {
  return s === ObjState.FLYING || s === ObjState.WOUNDED;
}

/**
 * True when the pilot's head points at the ground: heading left without
 * having rolled (orient 0), or heading right after a roll (orient 1). Exactly
 * vertical headings count as upright.
 */
export function planeIsInverted(ob: Obj): boolean {
  const c = COS(ob.angle);
  if (c === 0) {
    return false;
  }
  return ob.orient ? c > 0 : c < 0;
}

/**
 * Screen-relative pitch. When a pitch key is first pressed, decide which way
 * round the plane should turn so that the nose moves up (or down) on screen,
 * then keep turning that way for as long as the key is held. Holding pull-up
 * therefore still flies a full loop, while a fresh press when inverted goes
 * nose-up on screen instead of into the ground. Returns the command word
 * translated into the original pilot-relative keys.
 */
function screenPitchKeys(ob: Obj, key: number): number {
  const up = (key & K_FLAPU) !== 0;
  const down = (key & K_FLAPD) !== 0;
  const rest = key & ~(K_FLAPU | K_FLAPD);
  if (up === down) {
    ob.pitchHeld = 0;
    return rest;
  }
  const bit = up ? K_FLAPU : K_FLAPD;
  if (planeIsStalled(ob.state)) {
    // Stalled: pull-down homes the nose onto straight down (the recovery
    // attitude) and holds it there; pull-up turns away from it. The stall
    // flop mirrors the angle every few ticks, so this is re-evaluated each tick.
    ob.pitchHeld = bit;
    const target = (3 * ANGLES) / 4;
    if (ob.angle === target) {
      return up ? rest | (ob.orient ? K_FLAPD : K_FLAPU) : rest;
    }
    const ccwToTarget = (target - ob.angle + ANGLES) % ANGLES <= ANGLES / 2;
    const ccw = up ? !ccwToTarget : ccwToTarget;
    return rest | (ccw ? (ob.orient ? K_FLAPD : K_FLAPU) : ob.orient ? K_FLAPU : K_FLAPD);
  }
  if (ob.pitchHeld !== bit) {
    // New press: nose-up on screen is anticlockwise when heading right and
    // clockwise when heading left. Exactly vertical falls back to the
    // pilot-relative meaning so a loop in progress carries on.
    const c = COS(ob.angle);
    const ccwIsUp = c !== 0 ? c > 0 : ob.orient === 0;
    ob.pitchLatch = up === ccwIsUp ? 1 : -1;
  }
  ob.pitchHeld = bit;
  // In the original, pull-up turns anticlockwise when upright (orient 0)
  // and clockwise after a roll (orient 1).
  const ccwKey = ob.orient ? K_FLAPD : K_FLAPU;
  const cwKey = ob.orient ? K_FLAPU : K_FLAPD;
  return rest | (ob.pitchLatch > 0 ? ccwKey : cwKey);
}

// ---------------------------------------------------------------------------
// Helpers

/** Heading (0..7, in eighths of a turn) implied by an object's velocity. */
export function symangle(ob: Obj): number {
  const { dx, dy } = ob;
  if (dx === 0) {
    return dy > 0 ? 2 : 6;
  }
  if (dx > 0) {
    return dy < 0 ? 7 : dy > 0 ? 1 : 0;
  }
  return dy < 0 ? 5 : dy > 0 ? 3 : 4;
}

/** Slowly restock a counter while sitting at home. */
function topup(counter: number, max: number, countmove: number): number {
  if (counter === max) {
    return counter;
  }
  if (max < 20) {
    if (countmove % 20 === 0) {
      counter++;
    }
  } else {
    counter += (max / 100) | 0;
  }
  return clampMax(counter, max);
}

function refuel(g: Game, ob: Obj): void {
  ob.life = topup(ob.life, MAXFUEL, g.countmove);
  ob.rounds = topup(ob.rounds, MAXROUNDS, g.countmove);
  ob.bombs = topup(ob.bombs, MAXBOMBS, g.countmove);
  ob.missiles = topup(ob.missiles, MAXMISSILES, g.countmove);
  ob.bursts = topup(ob.bursts, MAXBURSTS, g.countmove);
}

/** Let computer planes whose territory we are in pick us as their target. */
function nearpln(g: Game, ob: Obj): void {
  const obx = ob.x;
  for (let obt = g.world.objtop; obt; obt = obt.next) {
    if (obt.type !== ObjType.PLANE || obt.faction === ob.faction) {
      continue;
    }
    if (obt.movef === movecomp) {
      const orig = obt.originalOb!;
      if (g.playmode !== PlayMode.COMPUTER || inRange(orig.territoryL, obx, orig.territoryR)) {
        const obc = obt.target;
        if (!obc || iabs(obx - obt.x) < iabs(obc.x - obt.x)) {
          obt.target = ob;
        }
      }
    }
  }
}

/** Nose over into a stall. */
function stallpln(ob: Obj): void {
  ob.ldx = ob.ldy = ob.orient = ob.dx = 0;
  ob.angle = (7 * ANGLES) / 8;
  ob.speed = 0;
  ob.dy = 0;
  ob.hitcount = STALLCOUNT;
  ob.state = ob.state === ObjState.WOUNDED ? ObjState.WOUNDSTALL : ObjState.STALLED;
  ob.athome = false;
}

// ---------------------------------------------------------------------------
// Player input

function interpret(g: Game, ob: Obj, key: number): void {
  ob.flaps = 0;
  ob.bombing = ob.bfiring = false;
  ob.mfiring = ob.firing = null;

  let state = ob.state;

  if (planeIsKilled(state) && state !== ObjState.FALLING) {
    return;
  }

  if (state !== ObjState.FALLING) {
    if (g.endstat) {
      if (g.endstat === EndStatus.LOSER && g.plyrplane) {
        gohome(g, ob);
      }
      return;
    }

    if (key & K_BREAK) {
      ob.life = QUIT;
      ob.home = false;
      if (ob.athome) {
        ob.state = state = ObjState.CRASHED;
        ob.hitcount = 0;
      }
      if (g.plyrplane) {
        g.quit = true;
      }
    }

    if (key & K_HOME) {
      if (state === ObjState.FLYING || state === ObjState.WOUNDED) {
        ob.home = true;
      }
    }
  }

  // Wounded planes respond to the controls only every other tick.
  if (!planeIsWounded(state) || (g.countmove & 1) !== 0) {
    if (key & K_FLAPU) {
      ob.flaps++;
      ob.home = false;
    }
    if (key & K_FLAPD) {
      ob.flaps--;
      ob.home = false;
    }
    // Flipping while parked on the runway was a bug in the original.
    if (key & K_FLIP && !ob.athome) {
      ob.orient = ob.orient ? 0 : 1;
      ob.home = false;
    }
    if (key & K_DEACC) {
      if (ob.accel) {
        ob.accel--;
      }
      ob.home = false;
    }
    if (key & K_ACCEL) {
      if (ob.accel < MAX_THROTTLE) {
        ob.accel++;
      }
      ob.home = false;
    }
  }

  if (key & K_SHOT && state < ObjState.FINISHED) {
    ob.firing = ob;
  }
  if (key & K_MISSILE && state < ObjState.FINISHED) {
    ob.mfiring = ob;
  }
  if (key & K_BOMB && state < ObjState.FINISHED) {
    ob.bombing = true;
  }
  if (key & K_STARBURST && state < ObjState.FINISHED) {
    ob.bfiring = true;
  }
  // K_SOUND only toggles audio; that is a presentation concern here.

  if (ob.home) {
    gohome(g, ob);
  }
}

// ---------------------------------------------------------------------------
// Move functions

export function moveplyr(g: Game, ob: Obj): boolean {
  g.compplane = false;
  g.plyrplane = g.player === ob.plrnum;
  g.endstat = g.consoleplayer.endsts;

  if (g.endstat) {
    g.endcount--;
    if (g.endcount <= 0) {
      if (g.playmode !== PlayMode.ASYNCH && !g.quit) {
        g.restart();
        return true;
      }
      g.end();
    }
  }

  let multkey = g.commands[ob.plrnum][g.countmove % MAX_NET_LAG];

  // "Harry keys": pitch keys swap whenever the sprite is mirrored.
  if ((multkey & K_HARRYKEYS) !== 0 && ob.orient) {
    if (multkey & (K_FLAPU | K_FLAPD)) {
      multkey ^= K_FLAPU | K_FLAPD;
    }
  }
  if (g.conf.screenPitch) {
    multkey = screenPitchKeys(ob, multkey);
  }

  interpret(g, ob, multkey);

  // Auto-roll: with the elevator neutral, face the way we are flying so the
  // plane is upright (the AI does the same in aim()).
  if (g.conf.autoRoll && ob.flaps === 0 && ob.dx !== 0 && planeIsFlying(ob.state) && !ob.athome) {
    ob.orient = ob.dx < 0 ? 1 : 0;
  }

  if (ob.state === ObjState.CRASHED && ob.hitcount <= 0) {
    if (g.playmode !== PlayMode.ASYNCH) {
      ob.crashcnt++;
    }
    if (
      g.endstat !== EndStatus.WINNER &&
      (ob.life <= QUIT || (g.playmode !== PlayMode.ASYNCH && ob.crashcnt >= MAXCRASH))
    ) {
      if (!g.endstat) {
        g.loser(ob);
      }
    } else {
      initplyr(g, ob, ob.originalOb!);
      if (g.endstat === EndStatus.WINNER) {
        g.winner(ob);
      }
    }
  }

  return movepln(g, ob);
}

export function movecomp(g: Game, ob: Obj): boolean {
  g.compplane = true;
  g.plyrplane = false;

  ob.flaps = 0;
  ob.bfiring = ob.bombing = false;
  ob.mfiring = null;

  g.endstat = ob.endsts;

  if (!g.dispcnt) {
    ob.firing = null;
  }

  switch (ob.state) {
    case ObjState.WOUNDED:
    case ObjState.WOUNDSTALL:
      if (g.countmove & 1) {
        break;
      }
    // fallthrough
    case ObjState.FLYING:
    case ObjState.STALLED:
      if (g.endstat) {
        gohome(g, ob);
        break;
      }
      if (!g.dispcnt) {
        swauto(g, ob);
      }
      break;

    case ObjState.CRASHED:
      ob.firing = null;
      if (ob.hitcount <= 0 && !g.endstat) {
        initcomp(g, ob, ob.originalOb!);
      }
      break;

    default:
      ob.firing = null;
      break;
  }

  return movepln(g, ob);
}

const xy = { x: 0, y: 0 };

/** Per-tick physics shared by player and computer planes. */
export function movepln(g: Game, ob: Obj): boolean {
  let state = ob.state;
  let stalled = false;
  let controlled = false;

  switch (state) {
    case ObjState.FINISHED:
    case ObjState.WAITING:
      return false;

    case ObjState.CRASHED:
      ob.hitcount--;
      break;

    case ObjState.FALLING: {
      // Tumble: the pilot can still influence the spin with the elevator.
      ob.hitcount -= 2;
      if (ob.dy < 0 && ob.dx) {
        if ((ob.orient !== 0) !== ob.dx < 0) {
          ob.hitcount -= ob.flaps;
        } else {
          ob.hitcount += ob.flaps;
        }
      }
      if (ob.hitcount <= 0) {
        if (ob.dy < 0) {
          if (ob.dx < 0) {
            ob.dx++;
          } else if (ob.dx > 0) {
            ob.dx--;
          } else {
            ob.orient++;
          }
        }
        if (ob.dy > -10) {
          ob.dy--;
        }
        ob.hitcount = FALLCOUNT;
      }
      ob.angle = symangle(ob) * 2;
      break;
    }

    case ObjState.STALLED:
    case ObjState.WOUNDSTALL: {
      const newstate = state === ObjState.STALLED ? ObjState.FLYING : ObjState.WOUNDED;
      // Recover only when pointing straight down and fast enough.
      stalled = ob.angle !== (3 * ANGLES) / 4 || ob.speed < g.gminspeed;
      if (!stalled) {
        ob.state = state = newstate;
      }
      controlled = true;
      break;
    }

    case ObjState.FLYING:
    case ObjState.WOUNDED:
      // Flying off the top of the world stalls the plane.
      stalled = ob.y >= MAX_Y;
      if (stalled) {
        if (g.playmode === PlayMode.NOVICE) {
          ob.angle = (3 * ANGLES) / 4;
          stalled = false;
        } else {
          stallpln(ob);
          state = ob.state;
        }
      }
      controlled = true;
      break;

    default:
      break;
  }

  if (controlled && !ob.goingsun) {
    if (ob.life <= 0 && !ob.athome && !planeIsKilled(state)) {
      // Out of fuel.
      hitpln(ob);
      scorepln(g, ob, ObjType.GROUND);
      return movepln(g, ob);
    }

    if (ob.firing) {
      initshot(g, ob, null);
    }
    if (ob.bombing) {
      initbomb(g, ob);
    }
    if (ob.mfiring) {
      initmiss(g, ob);
    }
    if (ob.bfiring) {
      initburst(g, ob);
    }

    let nangle = ob.angle;
    let nspeed = ob.speed;
    let update = ob.flaps !== 0;

    if (ob.flaps) {
      if (ob.orient) {
        nangle -= ob.flaps;
      } else {
        nangle += ob.flaps;
      }
      nangle = (nangle + ANGLES) % ANGLES;
    }

    // Speed only changes every fourth tick.
    if (!(g.countmove & 0x0003)) {
      if (!stalled && nspeed < g.gminspeed && g.playmode !== PlayMode.NOVICE) {
        nspeed--;
        update = true;
      } else {
        const limit = g.gminspeed + ob.accel + GRAVITY[nangle];
        if (nspeed < limit) {
          nspeed++;
          update = true;
        } else if (nspeed > limit) {
          nspeed--;
          update = true;
        }
      }
    }

    if (update) {
      if (ob.athome) {
        // On the runway: any input launches at minimum speed.
        nspeed = ob.accel || ob.flaps ? g.gminspeed : 0;
      } else if (nspeed <= 0 && !stalled) {
        if (g.playmode === PlayMode.NOVICE) {
          nspeed = 1;
        } else {
          stallpln(ob);
          return movepln(g, ob);
        }
      }

      ob.speed = nspeed;
      ob.angle = nangle;

      if (stalled) {
        ob.dx = ob.ldx = ob.ldy = 0;
        ob.dy = -nspeed;
      } else {
        setdxdy(ob, nspeed * COS(nangle), nspeed * SIN(nangle));
      }
    }

    if (stalled) {
      // Flop from side to side while falling.
      ob.hitcount--;
      if (ob.hitcount <= 0) {
        ob.orient = ob.orient ? 0 : 1;
        ob.angle = ((3 * ANGLES) / 2 - ob.angle) % ANGLES;
        ob.hitcount = STALLCOUNT;
      }
    }

    if (!g.compplane) {
      ob.life -= ob.speed;
    } else if (ob.life > 100) {
      ob.life -= ob.speed;
    }

    if (ob.speed) {
      ob.athome = false;
    }
  }

  // Pick the sprite for this tick (collision detection needs it too).
  if (ob.endsts === EndStatus.WINNER && ob.goingsun) {
    const frame = Math.min(3, (g.endcount / 18) | 0);
    ob.symbol = getSymset(g.symbols, 'swwinsym', frame).sym[0];
  } else if (ob.state === ObjState.FINISHED) {
    ob.symbol = null;
  } else if (ob.state === ObjState.FALLING && !ob.dx && ob.dy < 0) {
    ob.symbol = getSymset(g.symbols, 'swhitsym', ob.orient % 4).sym[0];
  } else if (ob.orient) {
    const a = (16 - ob.angle) % 16;
    ob.symbol = getSymset(g.symbols, 'swplnsym', a % 4).sym[4 + ((a / 4) | 0)];
  } else {
    ob.symbol = getSymset(g.symbols, 'swplnsym', ob.angle % 4).sym[(ob.angle / 4) | 0];
  }

  g.world.movexy(ob, g.level.maxX, xy);
  let { x } = xy;
  const { y } = xy;

  if (!inRange(0, x, g.level.maxX - 16)) {
    x = clampRange(0, x, g.level.maxX - 16);
    g.world.updateobjpos(ob);
  }

  if (!g.compplane && g.consoleplayer.endsts === EndStatus.PLAYING && !planeIsKilled(ob.state)) {
    nearpln(g, ob);
  }

  if (ob.bdelay) {
    ob.bdelay--;
  }
  if (ob.mdelay) {
    ob.mdelay--;
  }
  if (ob.bsdelay) {
    ob.bsdelay--;
  }

  if (!g.compplane && ob.athome && ob.state === ObjState.FLYING) {
    refuel(g, ob);
  }

  if (inRange(0, y, MAX_Y - 1)) {
    if (ob.state === ObjState.FALLING || planeIsWounded(ob.state)) {
      initsmok(g, ob);
    }
    return g.plyrplane || ob.state < ObjState.FINISHED;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Crash and hit

/** Hit the ground: sit crashed for a while, longer if near home. */
export function crashpln(ob: Obj): void {
  const orig = ob.originalOb!;
  if (ob.dx < 0) {
    ob.angle = (ob.angle + 2) % ANGLES;
  } else {
    ob.angle = (ob.angle + ANGLES - 2) % ANGLES;
  }
  ob.state = ObjState.CRASHED;
  ob.athome = false;
  ob.dx = ob.dy = ob.ldx = ob.ldy = ob.speed = 0;
  ob.hitcount =
    iabs(orig.x - ob.x) < SAFERESET && iabs(ob.origY - ob.y) < SAFERESET
      ? MAXCRCOUNT << 1
      : MAXCRCOUNT;
}

/** Shot down: start falling. */
export function hitpln(ob: Obj): void {
  ob.ldx = ob.ldy = 0;
  ob.hitcount = FALLCOUNT;
  ob.state = ObjState.FALLING;
  ob.athome = false;
}

// ---------------------------------------------------------------------------
// Creation

/** Create a plane, or reset an existing one on its runway. */
export function initpln(g: Game, obp: Obj | null, orig: OriginalOb): Obj {
  let ob: Obj;
  if (!obp) {
    if (g.numPlanes >= MAX_PLANES) {
      throw new Error('too many planes');
    }
    ob = g.world.allocobj();
    ob.originalOb = orig;
    g.planes[g.numPlanes++] = ob;
  } else {
    ob = obp;
  }

  if (obp && ob.state !== ObjState.CRASHED && !ob.athome) {
    // Just returned home safely: bank the sortie's achievements.
    getAwards(ob);
  }

  ob.flightscore = { planekills: 0, killscore: 0, valour: 0, combatwound: false };
  ob.type = ObjType.PLANE;

  // The runway is the highest ground under the 20 columns from the spawn X.
  ob.x = ob.originalOb!.x;
  let height = 0;
  for (let x = ob.x; x <= ob.x + 20; x++) {
    height = imax(height, g.ground[x]);
  }
  ob.y = height + 13;
  ob.origY = ob.y;

  ob.lx = ob.ly = ob.speed = ob.flaps = ob.accel = ob.hitcount = 0;
  ob.bdelay = ob.mdelay = ob.bsdelay = 0;
  setdxdy(ob, 0, 0);
  ob.orient = ob.originalOb!.orient;
  ob.angle = ob.orient ? ANGLES / 2 : 0;
  ob.target = ob.missiletarget = null;
  ob.firing = ob.mfiring = null;
  ob.bombing = ob.bfiring = ob.home = false;
  ob.symbol = getSymset(g.symbols, 'swplnsym', 0).sym[ob.orient ? 4 : 0];
  ob.athome = true;
  ob.onmap = true;
  ob.flightscore.combatwound = false;

  if (!obp || ob.state === ObjState.CRASHED) {
    ob.rounds = MAXROUNDS;
    ob.bombs = MAXBOMBS;
    ob.missiles = MAXMISSILES;
    ob.bursts = MAXBURSTS;
    ob.life = MAXFUEL;
  }
  if (!obp) {
    ob.score = g.takeSavedScore();
    ob.endsts = EndStatus.PLAYING;
    ob.target = null;
    g.world.insertx(ob, g.world.topobj);
  } else {
    g.world.insertx(ob, g.world.deletex(ob));
  }

  ob.state = ObjState.FLYING;
  ob.goingsun = false;
  return ob;
}

export function initplyr(g: Game, obp: Obj | null, orig: OriginalOb): Obj {
  const ob = initpln(g, obp, orig);
  if (!obp) {
    ob.movef = moveplyr;
    ob.faction = orig.faction;
    ob.clr = ob.faction;
    g.endcount = 0;
    ob.plrnum = g.numPlayers++;
    if (ob.plrnum === g.player) {
      g.consoleplayer = ob;
    }
  }
  return ob;
}

export function initcomp(g: Game, obp: Obj | null, orig: OriginalOb): Obj {
  const ob = initpln(g, obp, orig);
  if (!obp) {
    ob.movef = movecomp;
    ob.faction = Faction.PLAYER2;
    ob.clr = ob.faction;
  }
  if (g.playmode === PlayMode.SINGLE || g.playmode === PlayMode.NOVICE) {
    // No enemy planes in the solo modes.
    ob.state = ObjState.FINISHED;
    ob.onmap = false;
    g.world.deletex(ob);
  }
  return ob;
}
